<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div
    class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col pb-20">
    <!-- Modules -->
    <div class="flex-1 flex flex-col gap-5 sm:gap-6 container-padding pb-24">
      <SessionOption
        v-for="mod in modules"
        :key="mod.key"
        :title="mod.title"
        :icon="mod.icon"
        :showToggle="mod.showToggle"
        v-model="mod.toggleValue"
        :leftLabel="mod.leftLabel"
        :rightLabel="mod.rightLabel"
        @click="() => handleModuleClick(mod)" />
    </div>
  </div>
</template>

<script setup>
  import { reactive } from 'vue'
  import { useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import SessionOption from '@/components/ui/buttons/SessionOption.vue'
  import {
    Scale,
    Eye,
    Thermometer,
    Droplets,
    Stethoscope,
    AlertCircle,
    Syringe,
  } from 'lucide-vue-next'

  import { onMounted } from 'vue'
  import { useUiStore } from '@/stores/ui.store.js'

  const uiStore = useUiStore()

  onMounted(() => {
    uiStore.setHeader({
      title: t('views.session.title'),
      showBack: false,
      showAdd: false,
    })
  })

  const { t } = useI18n()
  const router = useRouter()

  const modules = reactive([
    {
      key: 'weight',
      title: t('views.session.options.weight'),
      icon: Scale,
      showToggle: true,
      toggleValue: false,
      leftLabel: t('views.session.photoMode'),
      rightLabel: t('views.session.manualMode'),
      get route() {
        return `/session/${
          this.toggleValue ? 'weight-manual' : 'weight-photo'
        }/groups`
      },
    },
    {
      key: 'temp',
      title: t('views.session.options.temperature'),
      icon: Thermometer,
      showToggle: false,
      toggleValue: false,
      route: '/session/temp/groups',
    },
    {
      key: 'wound',
      title: t('views.session.options.wound'),
      icon: AlertCircle,
      showToggle: false,
      toggleValue: false,
      route: '/session/wound/groups',
    },
    {
      key: 'treatment',
      title: t('views.session.options.treatment'),
      icon: Syringe,
      showToggle: false,
      toggleValue: false,
      route: '/session/treatment/groups',
    },
    {
      key: 'famacha',
      title: t('views.session.options.famacha'),
      icon: Eye,
      showToggle: false,
      toggleValue: false,
      route: '/session/famacha/groups',
    },
    {
      key: 'dag',
      title: t('views.session.options.dag'),
      icon: Droplets,
      showToggle: false,
      toggleValue: false,
      route: '/session/dag/groups',
    },
  ])

  function handleModuleClick(mod) {
    if (mod.route) router.push(mod.route)
  }
</script>
